import { jsxs, jsx } from "react/jsx-runtime";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { useTranslation } from "react-i18next";
import { useQuizStyleVariant } from "../shared/quiz-style-context.js";
import "react";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizTypeBadge } from "../shared/quiz-type-badge.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import "../../../context/DevToolsContext.js";
import { QUIZ_OX_STYLES } from "../shared/quiz-styles.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useBooleanQuiz } from "../hooks/use-boolean-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import Check from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/check.js";
import X from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/x.js";
function BooleanQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false
}) {
  const { t } = useTranslation("quiz");
  const variant = useQuizStyleVariant();
  const oxStyles = QUIZ_OX_STYLES[variant];
  const {
    selected,
    isSolved,
    canSubmit,
    correctAnswer,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSelect,
    handleSubmit
  } = useBooleanQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: /* @__PURE__ */ jsx(
          QuizTypeBadge,
          {
            quizId,
            typeLabel: t("types.boolean.label"),
            difficultyLevel
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50",
          "border border-gray-100 shadow-2xs",
          "text-2xl font-medium leading-relaxed text-gray-900"
        ),
        children: [
          /* @__PURE__ */ jsx(KatexText, { text: quiz.title }),
          quiz.problem && /* @__PURE__ */ jsx("div", { className: "mt-4 text-xl", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: oxStyles.container,
        role: "radiogroup",
        "aria-label": t("types.boolean.selectLabel"),
        children: [
          /* @__PURE__ */ jsxs(
            motion.button,
            {
              role: "radio",
              "aria-checked": selected === true,
              initial: { opacity: 0, x: -20 },
              animate: { opacity: 1, x: 0 },
              transition: { delay: 0.15 },
              onClick: () => handleSelect(true),
              disabled: isSolved || isViewSolved,
              className: cn(
                oxStyles.button,
                // 호버 효과
                !isSolved && !isViewSolved && oxStyles.buttonHover,
                // 선택 상태
                !isSolved && selected === true && oxStyles.buttonSelected,
                // 정답 (정답보기 모드 또는 내가 선택 && 맞음)
                isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) && oxStyles.buttonCorrect,
                // 오답 (맞아요를 선택했는데 정답이 틀려요, 정답보기 모드가 아닐 때만)
                isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers && oxStyles.buttonWrong,
                // 비활성화
                (isSolved || isViewSolved) && "cursor-not-allowed"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      oxStyles.mark,
                      // 선택
                      !isSolved && selected === true && oxStyles.markSelected,
                      // 정답
                      isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) && oxStyles.markCorrect,
                      // 오답
                      isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers && oxStyles.markWrong
                    ),
                    children: isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) ? /* @__PURE__ */ jsx(Check, { className: "w-10 h-10" }) : isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers ? /* @__PURE__ */ jsx(X, { className: "w-10 h-10" }) : "O"
                  }
                ),
                /* @__PURE__ */ jsx("span", { className: cn(
                  oxStyles.label,
                  !isSolved && selected === true && oxStyles.labelSelected,
                  isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) && oxStyles.labelCorrect,
                  isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers && oxStyles.labelWrong
                ), children: t("types.boolean.correct") })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            motion.button,
            {
              role: "radio",
              "aria-checked": selected === false,
              initial: { opacity: 0, x: 20 },
              animate: { opacity: 1, x: 0 },
              transition: { delay: 0.2 },
              onClick: () => handleSelect(false),
              disabled: isSolved || isViewSolved,
              className: cn(
                oxStyles.button,
                // 호버 효과
                !isSolved && !isViewSolved && oxStyles.buttonHover,
                // 선택 상태
                !isSolved && selected === false && oxStyles.buttonSelected,
                // 정답 (정답이 틀려요, 정답보기 모드 또는 내가 선택 && 맞음)
                isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) && oxStyles.buttonCorrect,
                // 오답 (틀려요를 선택했는데 정답이 맞아요, 정답보기 모드가 아닐 때만)
                isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers && oxStyles.buttonWrong,
                // 비활성화
                (isSolved || isViewSolved) && "cursor-not-allowed"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      oxStyles.mark,
                      // 선택
                      !isSolved && selected === false && oxStyles.markSelected,
                      // 정답 (정답이 틀려요)
                      isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) && oxStyles.markCorrect,
                      // 오답
                      isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers && oxStyles.markWrong
                    ),
                    children: isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) ? /* @__PURE__ */ jsx(Check, { className: "w-10 h-10" }) : isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers ? /* @__PURE__ */ jsx(X, { className: "w-10 h-10" }) : "X"
                  }
                ),
                /* @__PURE__ */ jsx("span", { className: cn(
                  oxStyles.label,
                  !isSolved && selected === false && oxStyles.labelSelected,
                  isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) && oxStyles.labelCorrect,
                  isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers && oxStyles.labelWrong
                ), children: t("types.boolean.incorrect") })
              ]
            }
          )
        ]
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? correctAnswer ? t("types.boolean.correct") : t("types.boolean.incorrect") : void 0,
        disableSound,
        isViewSolved
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && !hideExplanation && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  BooleanQuizAdult
};
//# sourceMappingURL=boolean-adult.js.map
